import React, {useEffect} from 'react';
import {classes, style} from './doppe-action-type-list-view-item.st.css';
import {
    ClampedText,
    cssEllipsis,
    Icon,
    ListViewItemRoot,
    RatioMaintainerIgnoreClassName,
    RichTextContent,
    RichTextView,
    useDynamicRef,
    useWasVisibleInViewport
} from '@wix/devzai-utils-react';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {arrayFirst, asArray, assertDefined, evaluateFunction} from '@wix/devzai-utils-common';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {
    DoppeActionLayout,
    doppeActionLayoutCanHideDescription,
    doppeActionLayoutCanHideImage,
    doppeActionLayoutCanHideTitle,
    doppeActionLayoutSupportsDescription,
    useBymoPageProps
} from '../../../client-server-common';
import {
    DoppeActionTypeSpeedDialWidgetIconChevronLeft,
    DoppeActionTypeSpeedDialWidgetIconChevronRight,
    DoppeActionTypeSpeedDialWidgetIconOpenLink
} from '../../doppe-action-type-speed-dial/doppe-action-type-speed-dial-widget/doppe-action-type-speed-dial-widget-icons';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    doppeViewerIntlDateTimeFormatShortMonthAndDay
} from '../../../client-server-common/doppe-viewer/doppe-viewer-intl-utils/doppe-viewer-intl-utils';
import {
    DoppeActionContentTipTapExtensions
} from '../../../client-server-common/doppe-tiptap-extensions/doppe-tiptap-extensions';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';
import {useDoppViewerIsRtl} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {
    useAssertWidgetRenderingContext,
    WidgetRenderingContextType
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import IconChevronDown
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronDown.svg';


export const DoppeActionTypeListViewItem = React.memo(function DoppeActionTypeListViewItem(
    props: {
        title: string;
        description: RichTextContent;
        image: WixMediaResource[] | WixMediaResource | null;
        price?: string;
        date?: Date;
        textOnImage?: string;
        layout: DoppeActionLayout;
        gridImageRatio?: number;
        centeredIcon?: Icon.Src;
        isOneItem: boolean;
        isLink?: boolean;
        ribbon?: string;
        hideImageWhenPossible?: boolean;
        hideTitleWhenPossible?: boolean;
        hideDescriptionWhenPossible?: boolean;
        actionContext: DoppeViewerActionContext<any>;
        innerItemIndex: number;
        innerItemId: string;
    }
) {
    const {
        image,
        price,
        date,
        textOnImage,
        title,
        description,
        layout,
        gridImageRatio,
        centeredIcon,
        ribbon,
        isOneItem = false,
        isLink = false,
        hideImageWhenPossible = false,
        hideTitleWhenPossible = false,
        hideDescriptionWhenPossible = false,
        actionContext,
        innerItemIndex,
        innerItemId,
    } = props;

    const isColumn = layout === DoppeActionLayout.Column;
    const doppeViewerLang = useBymoPageProps().lang;
    const calculatedImage = arrayFirst(asArray(image)) ?? null;

    const {
        widgetRenderingContextType
    } = useAssertWidgetRenderingContext();

    const itemRootRef = useDynamicRef<HTMLDivElement>();
    const wasVisible = useWasVisibleInViewport(itemRootRef);

    useEffect(() => {
        if (wasVisible && actionContext) {
            actionContext.reportInnerItemViewed(innerItemId, innerItemIndex);
        }
    }, [wasVisible, actionContext, innerItemId, innerItemIndex]);

    const isRtl = useDoppViewerIsRtl();

    return (
        <ListViewItemRoot
            className={style(classes.root, {
                layout: layout,
                isOneItem: isOneItem,
                hasImage: !!calculatedImage,
                widgetRenderingContextType: widgetRenderingContextType,
            }, FlexLayout.column.default, CommonClasses.button)}

        >
            <div
                ref={itemRootRef}
                className={style(classes.actionTypeListViewItemViewRootContent, isColumn ? FlexLayout.row.alignToCenter : FlexLayout.column.default)}
            >
                {hideImageWhenPossible && doppeActionLayoutCanHideImage(layout) ? null : (
                    <div
                        className={style(classes.linkImageContainer)}
                    >
                        <DoppePageMediaView
                            mediaResource={calculatedImage}
                            layoutSpec={evaluateFunction(() => {
                                switch (layout) {
                                    case DoppeActionLayout.Column: {
                                        return {widthHeightRatio: 1};
                                    }
                                    case DoppeActionLayout.Grid: {
                                        return {widthHeightRatio: gridImageRatio ?? 1.2};
                                    }
                                    case DoppeActionLayout.ImagesGrid: {
                                        return {widthHeightRatio: 1};
                                    }
                                    case DoppeActionLayout.LargeColumn: {
                                        return {widthHeightRatio: gridImageRatio ?? 1.5};
                                    }
                                    case DoppeActionLayout.Swiper: {
                                        return {widthHeightRatio: gridImageRatio ?? 1.2};
                                    }
                                    case DoppeActionLayout.Carousel: {
                                        return {widthHeightRatio: gridImageRatio ?? 1.2};
                                    }
                                    case DoppeActionLayout.CarouselSmall: {
                                        return {widthHeightRatio: gridImageRatio ?? 1.2};
                                    }
                                }
                            })}
                            widthSpec={evaluateFunction(() => {
                                switch (layout) {
                                    case DoppeActionLayout.Column: {
                                        return {width: 60}
                                    }
                                    case DoppeActionLayout.Grid: {
                                        return {contentRelativeWidth: 0.37}
                                    }
                                    case DoppeActionLayout.ImagesGrid: {
                                        return {contentRelativeWidth: 0.3}
                                    }
                                    case DoppeActionLayout.LargeColumn: {
                                        return {contentRelativeWidth: 0.8}
                                    }
                                    case DoppeActionLayout.Swiper: {
                                        return {contentRelativeWidth: 0.8}
                                    }
                                    case DoppeActionLayout.Carousel: {
                                        return {contentRelativeWidth: 0.7}
                                    }
                                    case DoppeActionLayout.CarouselSmall: {
                                        return {contentRelativeWidth: 0.35}
                                    }
                                }
                            })}
                        />
                        {
                            price && !isColumn ?
                                <div
                                    className={style(classes.priceOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis())}
                                >
                                    {price}
                                </div>
                                :
                                null
                        }
                        {
                            textOnImage ?
                                <div
                                    className={style(classes.textOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                                >
                                    <div
                                        className={style(classes.textOnImageText, {}, cssEllipsis(), FlexLayout.fillRemainingHorizontalSpace)}
                                    >
                                        {textOnImage ? textOnImage : doppeViewerIntlDateTimeFormatShortMonthAndDay(
                                            doppeViewerLang,
                                            assertDefined(date)
                                        )}
                                    </div>

                                </div>
                                :
                                date  ?
                                    <div
                                        className={style(classes.dateOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                                    >
                                        <div
                                            className={style(classes.dateText, {}, FlexLayout.fillRemainingHorizontalSpace)}
                                        >
                                            {doppeViewerIntlDateTimeFormatShortMonthAndDay(
                                                doppeViewerLang,
                                                date
                                            )}
                                        </div>

                                    </div>
                                    :
                                    null
                        }
                        {
                            centeredIcon ?
                                <Icon
                                    className={style(classes.ceneteredIcon, {}, RatioMaintainerIgnoreClassName)}
                                    icon={centeredIcon}
                                />
                                :
                                null
                        }
                        {
                            ribbon && !isColumn ?
                                <div
                                    className={style(classes.ribbonContainer, {dockedToTop: layout === DoppeActionLayout.ImagesGrid}, RatioMaintainerIgnoreClassName, FlexLayout.column.alignToStart)}
                                >
                                    <div
                                        className={style(classes.ribbon, {}, CommonClasses.ellipsis)}
                                    >
                                        {ribbon}
                                    </div>
                                </div>
                                :
                                null
                        }
                    </div>
                )}
                {layout !== DoppeActionLayout.ImagesGrid ? (
                    <div
                        className={style(classes.itemContent, FlexLayout.column.default, isColumn ? FlexLayout.fillRemainingHorizontalSpace : undefined)}
                    >
                        {
                            ribbon && isColumn ?
                                <div
                                    className={style(classes.inlineRibbonContainer, {}, FlexLayout.column.alignToStart)}
                                >
                                    <div
                                        className={style(classes.inlineRibbon, {}, CommonClasses.ellipsis)}
                                    >
                                        {ribbon}
                                    </div>
                                </div>
                                :
                                null
                        }
                        {hideTitleWhenPossible && doppeActionLayoutCanHideTitle(layout) ? null : (
                            <div
                                className={style(classes.itemProductTitle, isColumn ? CommonClasses.ellipsis : CommonClasses.lineClamp(2))}
                            >
                                {title}
                            </div>
                        )}
                        {!doppeActionLayoutSupportsDescription(layout) || (doppeActionLayoutCanHideDescription(layout) && hideDescriptionWhenPossible) || !description ? null : (
                            <>
                                <ClampedText
                                    className={style(classes.itemProductDescription, {})}
                                    maxLines={isColumn && !!price ? 1 : 2}
                                    clampText={true}
                                >
                                    {typeof description === 'string' ? description : (
                                        <RichTextView
                                            className={style(classes.richTextView, {})}
                                            richText={description}
                                            extensions={DoppeActionContentTipTapExtensions}
                                        />
                                    )}
                                </ClampedText>
                            </>
                        )}
                        {
                            price && isColumn ?
                                <div
                                    className={style(classes.actualPrice, {})}
                                >
                                    {price}
                                </div>
                                :
                                null
                        }
                    </div>
                ) : null}
                {isColumn ? (
                    <Icon
                        className={style(isLink ? classes.openLinkIcon : classes.openIcon, {})}
                        icon={isLink ? DoppeActionTypeSpeedDialWidgetIconOpenLink : widgetRenderingContextType === WidgetRenderingContextType.FullScreenModal ? !isRtl ? DoppeActionTypeSpeedDialWidgetIconChevronRight : DoppeActionTypeSpeedDialWidgetIconChevronLeft : IconChevronDown}
                    />
                ) : null}
            </div>
        </ListViewItemRoot>
    );
});


